/**
 * Hybrid System Integration Tool page.
 * @module HybridSystemIntegration
 */

import React, { useState } from 'react';
import { DndContext, PointerSensor, useSensor, useSensors } from '@dnd-kit/core';
import { PagesWrapper } from '../../../PagesWrapper';
import { useHybridSystemSimulation } from './hooks';
import { ComponentPalette } from './components/ComponentPalette';
import { MetricsDashboard } from './components/MetricsDashboard';
import { ParameterPanel } from './components/ParameterPanel';
import { SystemCanvas } from './components/SystemCanvas';
import { TimelineController } from './components/TimelineController';
import styles from './HybridSystemIntegration.module.css';

/**
 * Main Hybrid System Integration Tool component.
 * @returns {JSX.Element} Interactive hybrid system integration page.
 */
export function HybridSystemIntegrationTool() {
  const simulation = useHybridSystemSimulation();
  const [isPaletteOpen, setIsPaletteOpen] = useState(false);
  const sensors = useSensors(
    useSensor(PointerSensor, {
      activationConstraint: { distance: 8 },
    })
  );

  const handleDragEnd = (event) => {
    const nodeId = event.active?.data?.current?.nodeId;
    if (nodeId) {
      simulation.moveNode(nodeId, event.delta);
    }
  };

  return (
    <PagesWrapper>
      <DndContext sensors={sensors} onDragEnd={handleDragEnd}>
        <div className={styles.hybridPage}>
          <MetricsDashboard
            result={simulation.result}
            settings={simulation.settings}
            strategy={simulation.strategy}
          />

          <section className={styles.builderLayout}>
            <div className={styles.canvasColumn}>
              <SystemCanvas
                nodes={simulation.nodes}
                selectedNodeId={simulation.selectedNodeId}
                onSelectNode={simulation.setSelectedNodeId}
                result={simulation.result}
                settings={simulation.settings}
              />
            </div>

            <div className={styles.controlColumn}>
              <ParameterPanel
                selectedNode={simulation.selectedNode}
                settings={simulation.settings}
                strategy={simulation.strategy}
                storageLevel={simulation.storageLevel}
                onOpenPalette={() => setIsPaletteOpen(true)}
                onUpdateSetting={simulation.updateSetting}
                onUpdateSpec={simulation.updateNodeSpec}
                onStrategyChange={simulation.setStrategy}
                onStorageLevelChange={simulation.setStorageLevel}
                onRemoveComponent={simulation.removeComponent}
                onResetSystem={simulation.resetSystem}
              />

              <TimelineController
                settings={simulation.settings}
                isPlaying={simulation.isPlaying}
                scenarioKey={simulation.scenarioKey}
                onApplyScenario={simulation.applyScenario}
                onPlayingChange={simulation.setIsPlaying}
                onUpdateSetting={simulation.updateSetting}
              />
            </div>
          </section>

          <ComponentPalette
            open={isPaletteOpen}
            onClose={() => setIsPaletteOpen(false)}
            onAddComponent={simulation.addComponent}
          />
        </div>
      </DndContext>
    </PagesWrapper>
  );
}

export default HybridSystemIntegrationTool;
